/**
 * /admin/fleet — Fleet / Vehicle Mileage dashboard (Phase 1)
 *
 * Strict scope: read-only, batched aggregation, no extra queries per row.
 * Shows top cards, the vehicle table with today's status + 7-day km/day
 * sparkline, and a detail modal for a chosen vehicle.
 *
 * All day math uses Jamaica-time day strings coming from the server.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const fmtJMDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-JM", { timeZone: "America/Jamaica" });
  } catch { return iso; }
};

/** Inline SVG sparkline — no external chart library. */
function Sparkline({ points, width = 84, height = 22 }) {
  const nums = (points || []).filter((p) => typeof p === "number");
  if (nums.length < 2) {
    return <span data-testid="fleet-spark-empty" className="text-[10px] text-slate-400">Not enough data</span>;
  }
  const w = width;
  const h = height;
  const pad = 2;
  const max = Math.max(...nums, 1);
  const min = Math.min(...nums, 0);
  const range = max - min || 1;
  // X spacing over present values only (simplified, consistent with km/day idea)
  const step = nums.length > 1 ? (w - pad * 2) / (nums.length - 1) : 0;
  const toXY = (v, i) => [pad + step * i, h - pad - ((v - min) / range) * (h - pad * 2)];
  const d = nums.map(toXY).map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = toXY(nums[nums.length - 1], nums.length - 1);
  return (
    <svg
      data-testid="fleet-spark"
      width={w} height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="block align-middle"
      aria-label="7-day km/day sparkline"
    >
      <path d={d} fill="none" stroke="#0369a1" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill="#0369a1" />
    </svg>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted: { cls: "bg-emerald-100 text-emerald-800", label: "Submitted" },
    pending:   { cls: "bg-sky-100 text-sky-800", label: "Pending" },
    overdue:   { cls: "bg-rose-100 text-rose-800", label: "Overdue" },
    flagged:   { cls: "bg-amber-100 text-amber-800", label: "Flagged" },
  };
  const { cls, label } = map[status] || { cls: "bg-slate-100 text-slate-700", label: status || "—" };
  return <span data-testid={`fleet-row-status-${status}`} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
}

function Card({ testid, label, value, tone = "slate", hint }) {
  const toneMap = {
    sky: "border-sky-200 bg-sky-50 text-sky-900",
    slate: "border-slate-200 bg-white text-slate-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? "—"}</div>
      {hint ? <div className="mt-1 text-[11px] opacity-60">{hint}</div> : null}
    </div>
  );
}

function VehicleDetailDrawer({ vehicleId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!vehicleId) return;
    setData(null); setErr("");
    axios.get(`${BACKEND_URL}/api/admin/fleet/vehicles/${vehicleId}/detail`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load detail"));
  }, [vehicleId]);
  if (!vehicleId) return null;
  return (
    <div data-testid="fleet-detail-drawer" className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <div className="flex h-full w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Vehicle detail</div>
            <div className="font-semibold text-slate-900" data-testid="fleet-detail-title">
              {data?.vehicle ? `${data.vehicle.colour} ${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model} · ${data.vehicle.license_plate}` : "Loading…"}
            </div>
          </div>
          <button data-testid="fleet-detail-close" onClick={onClose} className="rounded-md border border-slate-300 px-2 py-1 text-xs">Close</button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
          {data?.vehicle && (
            <>
              <section className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Linkage</div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <dt className="text-slate-500">Ownership</dt>   <dd className="font-mono">{data.vehicle.ownership_type}</dd>
                  <dt className="text-slate-500">Driver</dt>      <dd>{data.vehicle.assigned_driver_name || "—"}</dd>
                  <dt className="text-slate-500">Driver ID</dt>   <dd className="font-mono text-[12px]">{data.vehicle.assigned_driver_id || "—"}</dd>
                  <dt className="text-slate-500">Last mileage</dt><dd>{data.vehicle.last_mileage != null ? `${data.vehicle.last_mileage.toLocaleString("en-JM")} km` : "—"}</dd>
                  <dt className="text-slate-500">Last JM date</dt><dd>{data.vehicle.last_mileage_jm_date || "—"}</dd>
                </dl>
              </section>
              <section className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">History</div>
                  <div className="text-[11px] text-slate-400">{data.history_count || 0} entries</div>
                </div>
                {(!data.history || data.history.length === 0) ? (
                  <div className="mt-2 text-sm text-slate-500" data-testid="fleet-detail-history-empty">No mileage submissions yet.</div>
                ) : (
                  <table className="mt-2 w-full text-sm">
                    <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="py-1">JM Date</th><th>Mileage</th><th>Submitted</th><th>Flags</th></tr>
                    </thead>
                    <tbody>
                      {data.history.map((h) => (
                        <tr key={h.id} data-testid={`fleet-detail-history-${h.id}`} className="border-t border-slate-100">
                          <td className="py-1">{h.jm_date}</td>
                          <td className="font-mono">{h.starting_mileage?.toLocaleString("en-JM")}</td>
                          <td className="text-[11px] text-slate-500">{fmtJMDate(h.submitted_at)}</td>
                          <td className="text-[11px] text-amber-700">{(h.review_flags || []).join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
              {data.missed_jm_dates && data.missed_jm_dates.length > 0 && (
                <section className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-800">Missed days</div>
                  <div data-testid="fleet-detail-missed" className="mt-1 text-sm text-rose-900">
                    {data.missed_jm_dates.slice(0, 14).join(", ")}{data.missed_jm_dates.length > 14 ? `, … (${data.missed_jm_dates.length} total)` : ""}
                  </div>
                </section>
              )}
              <section className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Reminders (recent 10)</div>
                {(!data.reminders || data.reminders.length === 0) ? (
                  <div className="mt-2 text-sm text-slate-500">No reminder events.</div>
                ) : (
                  <ul className="mt-2 space-y-1 text-[12px]">
                    {data.reminders.map((r) => (
                      <li key={r.id} className="flex items-center justify-between">
                        <span className="font-mono">{r.jm_date}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.resolved ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {r.kind} · {r.resolved ? "resolved" : "queued"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminFleetDashboard() {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("all");
  const [ownership, setOwnership] = useState("all");
  const [openVehicleId, setOpenVehicleId] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const params = {};
      if (ownership !== "all") params.ownership_type = ownership;
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/fleet/dashboard`, {
        headers: authHeaders(), params,
      });
      setSnap(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load fleet dashboard (admin role required).");
    }
  }, [ownership]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!snap?.rows) return [];
    if (status === "all") return snap.rows;
    return snap.rows.filter((r) => r.today_status === status);
  }, [snap, status]);

  return (
    <div data-testid="admin-fleet-dashboard" className="min-h-screen bg-slate-50 p-5">
      <div className="mx-auto max-w-6xl">
        <header className="mb-4 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Fleet · Vehicle Mileage</h1>
            <div className="text-xs text-slate-500">
              Jamaica time · {snap?.jm_date || "—"} · cutoff 0{snap?.cutoff_hour_jm ?? 9}:00
            </div>
          </div>
          <button
            data-testid="admin-fleet-refresh"
            onClick={load}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
          >Refresh</button>
        </header>

        {err && <div data-testid="admin-fleet-error" className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Card testid="admin-fleet-card-tracked"   tone="sky"     label="Vehicles tracked" value={snap?.cards?.vehicles_tracked}/>
          <Card testid="admin-fleet-card-submitted" tone="emerald" label="Submitted today"  value={snap?.cards?.submitted_today}/>
          <Card testid="admin-fleet-card-missing"   tone="amber"   label="Missing today"    value={snap?.cards?.missing_today}/>
          <Card testid="admin-fleet-card-overdue"   tone="rose"    label="Overdue"          value={snap?.cards?.overdue}/>
          <Card testid="admin-fleet-card-review"    tone="slate"   label="Needs review"     value={snap?.cards?.review}/>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1">
            <span className="text-slate-500">Status</span>
            <select data-testid="admin-fleet-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1">
              <option value="all">All</option>
              <option value="submitted">Submitted</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span className="text-slate-500">Ownership</span>
            <select data-testid="admin-fleet-filter-ownership" value={ownership} onChange={(e) => setOwnership(e.target.value)}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1">
              <option value="all">All</option>
              <option value="LIANTIAO_OWNED">Liantiao-owned</option>
              <option value="CONTRACTOR">Contractor</option>
            </select>
          </label>
        </div>

        <section className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Vehicle</th>
                <th className="px-3 py-2">Plate</th>
                <th className="px-3 py-2">Driver</th>
                <th className="px-3 py-2">Today</th>
                <th className="px-3 py-2">Last mileage</th>
                <th className="px-3 py-2">Δ prev</th>
                <th className="px-3 py-2">Reminder</th>
                <th className="px-3 py-2">7-day trend</th>
                <th className="px-3 py-2">Flags</th>
              </tr>
            </thead>
            <tbody data-testid="admin-fleet-rows">
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-500">No vehicles match this filter.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id}
                    data-testid={`admin-fleet-row-${r.license_plate}`}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    onClick={() => setOpenVehicleId(r.id)}>
                  <td className="px-3 py-2"><div className="font-medium text-slate-900">{r.colour} {r.year} {r.make} {r.model}</div><div className="text-[11px] text-slate-500">{r.ownership_type}</div></td>
                  <td className="px-3 py-2 font-mono text-[12px]" data-testid={`admin-fleet-row-plate-${r.license_plate}`}>{r.license_plate}</td>
                  <td className="px-3 py-2">{r.assigned_driver_name || <span className="text-slate-400">unassigned</span>}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.today_status} /></td>
                  <td className="px-3 py-2 font-mono">{r.last_mileage != null ? r.last_mileage.toLocaleString("en-JM") : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 font-mono">{r.delta_vs_prev != null ? `${r.delta_vs_prev.toLocaleString("en-JM")} km` : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-[11px]">{r.reminder ? (r.reminder.resolved ? "resolved" : "queued") : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2"><Sparkline points={r.spark_km_per_day} /></td>
                  <td className="px-3 py-2 text-[11px] text-amber-700">{(r.review_flags || []).join(", ") || <span className="text-slate-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      <VehicleDetailDrawer vehicleId={openVehicleId} onClose={() => setOpenVehicleId(null)} />
    </div>
  );
}
